import { useNavigate } from "react-router-dom";
import ModelsHeader from "./ModelsHeader";
import { PrimaryButton } from "../Button/Buttons";
import { Plus } from "lucide-react";
import ROUTES from "../../router/routes";

/**
 * Shared layout shell for all Settings (and similar) pages.
 *
 * Props:
 *   title       – Page title shown in the header
 *   description – Optional sub-heading shown in the content area
 *   action      – Optional { label, onClick } — renders a PrimaryButton alongside description
 *   onBack      – Optional back-button handler. Defaults to navigating to /settings.
 *                 Pass null explicitly to suppress the back button.
 *   maxWidth    – "3xl" | "4xl" | "5xl" | "6xl"  (default: "4xl")
 *   noPadding   – Skip the inner max-w wrapper (for full-bleed layouts)
 */

const MAX_WIDTH_CLASS = {
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
};

const SettingsPageShell = ({ title, description, action, onBack, maxWidth = "4xl", noPadding = false, children }) => {
  const navigate = useNavigate();
  const mwClass = MAX_WIDTH_CLASS[maxWidth] ?? "max-w-4xl";

  // Default: back to settings landing. Pass onBack={null} to suppress.
  const resolvedOnBack = onBack !== null ? (onBack ?? (() => navigate(ROUTES.SETTINGS.ROOT))) : undefined;

  return (
    <div className="w-full h-full">
      <ModelsHeader title={title} onBack={resolvedOnBack} />
      <div className="h-[calc(100%-80px)] overflow-auto show-scrollbar">
        {noPadding ? (
          children
        ) : (
          <div className={`${mwClass} px-6 mx-auto py-6`}>
            {(description || action) && (
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex-1 min-w-0">
                  {description && (
                    <p className="text-ink-gray-500 dark:text-ink-gray-400 text-sm">{description}</p>
                  )}
                </div>
                {action && (
                  <div className="flex-shrink-0">
                    <PrimaryButton onClick={action.onClick} className="flex items-center gap-1.5">
                      <Plus className="w-4 h-4" />
                      {action.label}
                    </PrimaryButton>
                  </div>
                )}
              </div>
            )}
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPageShell;
